import { useEffect, useRef, useState } from 'react';

import type { default as Candidate } from 'model/Candidate/Candidate';

import { usePageContext } from 'hooks/shared/usePageContext';
import { getDialogKeyByPageContext } from 'shared/legacy/contexts/DialogContext/getDialogKeyByPageContext';
import { useCampaignStore } from 'stores/campaign/campaign.store';
import { useGlobalDialogsStore } from 'stores/global-dialogs/global-dialogs.store';
import { GlobalDialogType } from 'stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialog } from 'stores/global-dialogs/hooks/useGlobalDialog';
import { useGlobalDialogData } from 'stores/global-dialogs/hooks/useGlobalDialogData';
import { useGlobalDialogKey } from 'stores/global-dialogs/hooks/useGlobalDialogKey';
import { getPageContext } from 'utils/getPageContext';

export const useAutomaticallyOpenDialog = (candidate: Candidate | null | undefined) => {
  const removeDialog = useGlobalDialogsStore((state) => state.remove);
  const phoneGlobalDialog = useGlobalDialog(GlobalDialogType.CampaignPhoneReachOut);
  const activeDialogKey = useGlobalDialogKey(GlobalDialogType.CampaignPhoneReachOut);

  const isCandidateInvisible = candidate === null;
  const selectedCampaignId = useCampaignStore((state) => state.campaign?.id);

  const key = getDialogKeyByPageContext(
    GlobalDialogType.CampaignPhoneReachOut,
    getPageContext(),
    selectedCampaignId?.toString() ?? 'fallback-key',
  );
  const dialogData = useGlobalDialogData({ dialogType: GlobalDialogType.CampaignPhoneReachOut, key });
  const pageContext = usePageContext();
  const lastSelectedCampaignId = useRef(selectedCampaignId);
  const [wasAutomaticallyOpened, setWasAutomaticallyOpened] = useState(false);

  useEffect(() => {
    // If there's no data, we don't need to open the dialog
    if (!dialogData) return;

    // If the candidate is invisible, we need to close the dialog
    if (isCandidateInvisible && selectedCampaignId) {
      phoneGlobalDialog.close();
      removeDialog(GlobalDialogType.CampaignPhoneReachOut, selectedCampaignId.toString());
    }

    // If the dialog is already open with the same key, we don't need to open it again
    if (activeDialogKey === key.toString()) return;

    // If the dialog is not open, we need to open it
    if (activeDialogKey === null) {
      lastSelectedCampaignId.current = selectedCampaignId;

      phoneGlobalDialog.open({ key: key.toString(), payload: dialogData });

      // If the dialog was automatically opened, we need to set the state
      setWasAutomaticallyOpened(true);
    }
  }, [dialogData, isCandidateInvisible, activeDialogKey, selectedCampaignId]);

  useEffect(() => {
    // If the page context changes, we need to close the dialog
    if (pageContext) {
      phoneGlobalDialog.close();
    }
  }, [pageContext]);

  useEffect(() => {
    // If the selected campaign changes, we need to close the dialog
    if (selectedCampaignId && lastSelectedCampaignId.current && selectedCampaignId !== lastSelectedCampaignId.current) {
      phoneGlobalDialog.close();
    }
  }, [selectedCampaignId]);

  useEffect(() => {
    // If the dialog was automatically opened and the active dialog key changes, we need to reset the state
    if (wasAutomaticallyOpened && !activeDialogKey) {
      setWasAutomaticallyOpened(false);
    }
  }, [wasAutomaticallyOpened, activeDialogKey]);

  return { wasAutomaticallyOpened };
};
